'use client'

import * as React from 'react'
import { useState } from 'react'
import { TrashIcon } from '@phosphor-icons/react'
import type { Icon } from '@phosphor-icons/react'
import {
  SELECT_TRIGGER_CLASSES,
  ICON_SIZES,
  FOCUS_STYLES,
} from '@/ui/design-tokens'
import { ResponsiveSelector } from '@/ui/responsive-selector'
import { cn } from '@/lib/tailwind'

interface PropertyOption<T> {
  value: T
  label: string
  Icon?: Icon
}

interface SingleSelectPropertyProps<T extends string | number> {
  /** Display label */
  label: string
  /** Icon to show in the trigger */
  Icon: Icon
  /** Available options */
  options: PropertyOption<T>[]
  /** Currently selected value (null if not selected) */
  value: T | null
  /** Called when value changes */
  onChange: (value: T | null) => void
  /** Whether to allow removing the selection */
  allowRemove?: boolean
  /** Whether the property is disabled */
  disabled?: boolean
}

/**
 * A property field that allows selecting a single value from a list.
 * Uses ResponsiveSelector: popover on desktop, bottom sheet on mobile.
 * When no value is selected, shows with dashed border styling.
 * When a value is selected, shows with filled styling.
 */
export function SingleSelectProperty<T extends string | number>({
  label,
  Icon: IconComponent,
  options,
  value,
  onChange,
  allowRemove = true,
  disabled = false,
}: SingleSelectPropertyProps<T>) {
  const [open, setOpen] = useState(false)
  const selectedOption = options.find((opt) => opt.value === value)
  const hasValue = value !== null && selectedOption !== undefined

  // Empty state: no options available
  if (options.length === 0) {
    return (
      <button
        type="button"
        disabled
        className={cn(
          SELECT_TRIGGER_CLASSES.dashed,
          FOCUS_STYLES.ringCompact,
          'cursor-not-allowed opacity-50',
        )}
      >
        <IconComponent className={`${ICON_SIZES.compact} shrink-0`} />
        <span>No options</span>
      </button>
    )
  }

  const handleSelect = (opt: PropertyOption<T>) => {
    onChange(opt.value)
    setOpen(false)
  }

  const handleRemove = () => {
    onChange(null)
    setOpen(false)
  }

  return (
    <ResponsiveSelector
      open={open}
      onOpenChange={setOpen}
      ariaLabel={label}
      trigger={
        <button
          type="button"
          disabled={disabled}
          className={cn(
            hasValue
              ? SELECT_TRIGGER_CLASSES.filled
              : SELECT_TRIGGER_CLASSES.dashed,
            FOCUS_STYLES.ringCompact,
            'active:scale-[0.98]',
          )}
        >
          {!hasValue && (
            <IconComponent className={`${ICON_SIZES.compact} shrink-0`} />
          )}
          <span>{hasValue ? selectedOption.label : label}</span>
        </button>
      }
    >
      <div className="space-y-0.5">
        {options.map((option) => {
          const OptionIcon = option.Icon
          const isSelected = value === option.value
          return (
            <button
              key={String(option.value)}
              type="button"
              onClick={() => handleSelect(option)}
              className={cn(
                'flex min-h-[44px] w-full items-center gap-1.5 rounded-sm px-2 py-1.5 text-left text-sm transition-colors sm:min-h-[32px]',
                isSelected
                  ? 'bg-accent text-accent-foreground'
                  : 'hover:bg-muted',
              )}
            >
              {OptionIcon && <OptionIcon className={ICON_SIZES.compact} />}
              {option.label}
            </button>
          )
        })}
        {allowRemove && hasValue && (
          <>
            <div className="bg-border my-1 h-px" />
            <button
              type="button"
              onClick={handleRemove}
              className="text-destructive hover:bg-muted flex min-h-[44px] w-full items-center gap-1.5 rounded-sm px-2 py-1.5 text-left text-sm transition-colors sm:min-h-[32px]"
            >
              <TrashIcon className={ICON_SIZES.compact} />
              Remove
            </button>
          </>
        )}
      </div>
    </ResponsiveSelector>
  )
}
